import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import TeamInviteLinkCard from "@/pages/coach/TeamInviteLinkCard";
import RecoveryVideoOverridesCard from "@/pages/coach/RecoveryVideoOverridesCard";
import StaffCard from "@/pages/coach/StaffCard";
import DigestScheduleCard from "@/pages/coach/DigestScheduleCard";
import NotificationPrefsCard from "@/pages/coach/NotificationPrefsCard";
import PushSettingsCard from "@/components/PushSettingsCard";
import TierUpgradeTimeline from "@/components/TierUpgradeTimeline";
import EnterpriseBrandingCard from "@/components/EnterpriseBrandingCard";

const BASELINE_GROUPS = [
  { key: "personal_metadata", title: "Personal Metadata", note: "Year, dominant arm, position, injury log" },
  { key: "mobility_rom", title: "Mobility & ROM", note: "External rotation, internal rotation, total arc" },
  { key: "humeral_torsion", title: "Humeral Torsion", note: "Dominant / non-dominant / difference" },
  { key: "musculoskeletal_strength", title: "Rotational Strength", note: "ER & IR strength + LSI %" },
  { key: "grip_strength", title: "Grip 90/90", note: "Dominant / non-dominant / LSI %" },
  { key: "functional_power", title: "Functional Power", note: "Y-Balance, broad jump, vertical leap" },
  { key: "speed_agility", title: "Speed & Agility", note: "60yd, splits, 5-10-5, L-drill" },
  { key: "ball_flight", title: "Ball Flight", note: "Pull downs, mound velo, FlightScope, Rapsodo" },
];

const DAILY_GROUPS = [
  { key: "arm_health", title: "Arm Health 1–5" },
  { key: "body_soreness", title: "Body Soreness 1–5" },
  { key: "sleep_quality", title: "Sleep Quality 1–5" },
  { key: "mental_energy", title: "Mental Energy 1–5" },
  { key: "scheduled_hybrid", title: "Scheduled Hybrid" },
  { key: "throwing_environment", title: "Throwing Environment" },
  { key: "max_distance", title: "Max Distance (ft)" },
  { key: "throw_volume", title: "Throw Volume" },
  { key: "chest_accuracy", title: "75% Chest Accuracy" },
  { key: "pain_during", title: "Pain During Session" },
  { key: "post_fatigue", title: "Post-Throw Joint Fatigue" },
];

const WEIGHT_KEYS = [
  { key: "recovery",   label: "Recovery (HRV)",        note: "HRV vs. 14-day baseline" },
  { key: "sleep",      label: "Sleep",                 note: "Hours vs. team target" },
  { key: "load",       label: "Workload (A:C ratio)",  note: "Acute:Chronic throw load" },
  { key: "subjective", label: "Subjective (self-log)", note: "Arm/soreness/energy" },
];

export default function CoachSettings() {
  const { team, fetchTeam } = useAuth();
  const [draft, setDraft] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (team) setDraft({ ...team }); }, [team]);

  if (!draft) return <div className="text-sm text-white/50">Loading…</div>;

  const setField = (k, v) => setDraft({ ...draft, [k]: v });
  const toggleBaseline = (k) =>
    setDraft({ ...draft, baseline_toggles: { ...draft.baseline_toggles, [k]: !draft.baseline_toggles[k] } });
  const toggleDaily = (k) =>
    setDraft({ ...draft, daily_log_toggles: { ...draft.daily_log_toggles, [k]: !draft.daily_log_toggles[k] } });

  const addEmail = () => {
    const e = newEmail.trim().toLowerCase();
    if (!e || draft.staff_emails.includes(e)) return;
    setDraft({ ...draft, staff_emails: [...draft.staff_emails, e] });
    setNewEmail("");
  };
  const removeEmail = (e) =>
    setDraft({ ...draft, staff_emails: draft.staff_emails.filter((x) => x !== e) });

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/teams/me/settings", {
        name: draft.name,
        primary_color: draft.primary_color,
        secondary_color: draft.secondary_color,
        accent_color: draft.accent_color,
        staff_emails: draft.staff_emails,
        baseline_toggles: draft.baseline_toggles,
        daily_log_toggles: draft.daily_log_toggles,
        readiness_thresholds: draft.readiness_thresholds,
        component_weights: draft.component_weights,
        screening_assessment_mode: draft.screening_assessment_mode,
      });
      await fetchTeam();
      toast.success("Team settings saved");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const inputCls = "bg-[var(--ink-1)] border-[var(--ink-border)] text-white";

  return (
    <div className="space-y-8 max-w-4xl" data-testid="coach-settings-page">
      <div>
        <div className="text-[10px] tracking-[0.3em] font-bold text-white/40 uppercase">Team Settings</div>
        <h1 className="font-display font-black text-3xl md:text-5xl tracking-tighter mt-1 text-white">
          Brand &amp; Modules
        </h1>
      </div>

      <TierUpgradeTimeline />
      <EnterpriseBrandingCard />

      <section className="rounded-xl ink-card p-6" data-testid="team-identity-section">
        <h2 className="font-display font-bold text-lg text-white">Team Identity</h2>
        <p className="text-sm text-white/50 mt-1">The name your athletes see on their portal and how the team is labeled to admins.</p>
        <div className="mt-5">
          <Label className="text-white/70">Team Name</Label>
          <Input value={draft.name || ""} onChange={(e) => setField("name", e.target.value)} placeholder="Southern University of New Orleans (SUNO)" className={`mt-2 h-12 ${inputCls}`} data-testid="team-name-input" />
        </div>
      </section>

      <TeamInviteLinkCard />

      <section className="rounded-xl ink-card p-6">
        <h2 className="font-display font-bold text-lg text-white">Brand Colors</h2>
        <p className="text-sm text-white/50 mt-1">Hex codes drive the entire player portal and coach console.</p>
        <div className="grid md:grid-cols-3 gap-5 mt-5">
          {[
            ["primary_color", "Primary"],
            ["secondary_color", "Secondary"],
            ["accent_color", "Canvas Accent"],
          ].map(([k, label]) => (
            <div key={k}>
              <Label className="text-white/70">{label}</Label>
              <div className="flex items-center gap-2 mt-2">
                <input type="color" value={draft[k]} onChange={(e) => setField(k, e.target.value)}
                  className="h-11 w-14 rounded-md border border-[var(--ink-border)] cursor-pointer bg-transparent" data-testid={`color-input-${k}`} />
                <Input value={draft[k]} onChange={(e) => setField(k, e.target.value)} className={`h-11 font-mono-num ${inputCls}`} />
              </div>
            </div>
          ))}
        </div>
        <div className="mt-5 rounded-lg p-5 border border-[var(--ink-border)]" style={{ backgroundColor: draft.primary_color }}>
          <div className="font-display font-black text-3xl">
            <span style={{ color: "#ffffff" }}>Arm</span>
            <span style={{ color: draft.secondary_color }}>IQ</span>
            <span className="text-white/40 text-base font-normal ml-3">Live preview</span>
          </div>
        </div>
      </section>

      {/* Screening Assessment Mode — HC choice between in-app vs local AT/PT */}
      <section className="rounded-xl ink-card p-6" data-testid="screening-mode-section">
        <h2 className="font-display font-bold text-lg text-white">Functional Movement &amp; Range-of-Motion Testing</h2>
        <p className="text-sm text-white/50 mt-1">
          Choose how your team's FMS &amp; GIRD baseline data enters ArmIQ. You can change this at any time — switching modes does not delete prior results.
        </p>
        <div className="grid md:grid-cols-2 gap-3 mt-5">
          {[
            { key: "armiq_builtin", title: "ArmIQ Functional Movement", lead: "Built-in", body: "Athletes complete the markerless FMS battery and digital goniometer GIRD test on their phones during onboarding. ArmIQ auto-scores the 7 movements + computes side asymmetry." },
            { key: "local_staff",  title: "Local AT / PT Staff", lead: "External", body: "Your athletic training or physical therapy staff performs the screening with their own protocols. Coaches enter the resulting values directly on the Player Detail page. Athlete self-test wizards are hidden." },
          ].map((opt) => {
            const active = (draft.screening_assessment_mode || "armiq_builtin") === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => setField("screening_assessment_mode", opt.key)}
                data-testid={`screening-mode-${opt.key}`}
                className="text-left rounded-lg border p-4 transition-all"
                style={{
                  borderColor: active ? "var(--brand-secondary)" : "rgba(255,255,255,0.1)",
                  backgroundColor: active ? "rgba(217,4,41,0.08)" : "rgba(255,255,255,0.02)",
                }}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: active ? "var(--brand-secondary)" : "rgba(255,255,255,0.45)" }}>
                    {opt.lead}
                  </span>
                  {active && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ backgroundColor: "var(--brand-secondary)", color: "#fff" }}>Active</span>}
                </div>
                <div className="font-display font-bold text-white text-base mt-2">{opt.title}</div>
                <p className="text-[12px] text-white/65 mt-1.5 leading-relaxed">{opt.body}</p>
              </button>
            );
          })}
        </div>
      </section>

      <section className="rounded-xl ink-card p-6">
        <h2 className="font-display font-bold text-lg text-white">Critical Shutdown Recipients</h2>
        <p className="text-sm text-white/50 mt-1">Emails added here receive automated alerts when a player triggers a CRITICAL state.</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {draft.staff_emails.map((e) => (
            <span key={e} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm text-white" style={{ backgroundColor: "var(--ink-3)" }} data-testid={`staff-email-${e}`}>
              {e}
              <button onClick={() => removeEmail(e)} className="text-white/40 hover:text-red-400" data-testid={`remove-staff-${e}`}>
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
        <div className="mt-3 flex gap-2 max-w-md">
          <Input type="email" placeholder="staff@team.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className={inputCls} data-testid="add-email-input" />
          <Button variant="outline" onClick={addEmail} className="border-white/15 text-white bg-white/5 hover:bg-white/10" data-testid="add-email-button"><Plus className="h-4 w-4" /></Button>
        </div>
      </section>

      <section className="rounded-xl ink-card p-6">
        <h2 className="font-display font-bold text-lg text-white">Baseline Modules (Table A)</h2>
        <p className="text-sm text-white/50 mt-1">Disable groups you don't track. They will be hidden from athlete profiles.</p>
        <div className="grid md:grid-cols-2 gap-3 mt-5">
          {BASELINE_GROUPS.map((g) => (
            <label key={g.key} className="flex items-center justify-between rounded-lg border border-[var(--ink-border)] p-4 cursor-pointer hover:border-[var(--ink-border-strong)]" style={{ backgroundColor: "var(--ink-1)" }} data-testid={`baseline-toggle-${g.key}`}>
              <div className="pr-3">
                <div className="font-semibold text-white text-sm">{g.title}</div>
                <div className="text-xs text-white/50 mt-0.5">{g.note}</div>
              </div>
              <Switch checked={!!draft.baseline_toggles[g.key]} onCheckedChange={() => toggleBaseline(g.key)} />
            </label>
          ))}
        </div>
      </section>

      <section className="rounded-xl ink-card p-6">
        <h2 className="font-display font-bold text-lg text-white">Daily Log Modules (Table B)</h2>
        <p className="text-sm text-white/50 mt-1">Hide fields you don't want athletes to see in the daily log.</p>
        <div className="grid md:grid-cols-3 gap-3 mt-5">
          {DAILY_GROUPS.map((g) => (
            <label key={g.key} className="flex items-center justify-between rounded-lg border border-[var(--ink-border)] p-3 cursor-pointer hover:border-[var(--ink-border-strong)]" style={{ backgroundColor: "var(--ink-1)" }} data-testid={`daily-toggle-${g.key}`}>
              <div className="font-medium text-white/90 text-sm">{g.title}</div>
              <Switch checked={!!draft.daily_log_toggles[g.key]} onCheckedChange={() => toggleDaily(g.key)} />
            </label>
          ))}
        </div>
      </section>

      <section className="rounded-xl ink-card p-6">
        <h2 className="font-display font-bold text-lg text-white">Readiness Engine Thresholds</h2>
        <p className="text-sm text-white/50 mt-1">Tune how aggressively the engine flags HRV drops, load imbalance, and sleep debt.</p>
        <div className="grid md:grid-cols-2 gap-4 mt-5">
          {[
            ["hrv_drop_pct_hold", "HRV drop % → auto-HOLD", "%"],
            ["ac_ratio_low", "Acute:Chronic — too-low threshold", "ratio"],
            ["ac_ratio_high", "Acute:Chronic — too-high threshold", "ratio"],
            ["sleep_target_hours", "Sleep target", "hrs"],
            ["sleep_min_hours", "Sleep debt floor", "hrs"],
          ].map(([k, label, unit]) => (
            <div key={k} className="rounded-lg border border-[var(--ink-border)] p-4" style={{ backgroundColor: "var(--ink-1)" }}>
              <Label className="text-white/70">{label}</Label>
              <div className="flex items-center gap-2 mt-2">
                <Input type="number" step="0.1"
                  value={(draft.readiness_thresholds || {})[k] ?? ""}
                  onChange={(e) => setDraft({ ...draft, readiness_thresholds: { ...(draft.readiness_thresholds || {}), [k]: Number(e.target.value) } })}
                  className={`h-10 font-mono-num ${inputCls}`} data-testid={`threshold-${k}`} />
                <span className="text-xs text-white/40">{unit}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-xl ink-card p-6" data-testid="weights-section">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display font-bold text-lg text-white">Readiness Engine Weights</h2>
            <p className="text-sm text-white/50 mt-1">
              How much each component contributes to the daily Readiness Score (0–100). The engine normalises your weights to 100 at score-time, so any total works.
            </p>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Total</div>
            <div className="font-display font-black text-2xl text-white font-mono-num" data-testid="weights-total">
              {WEIGHT_KEYS.reduce((s, w) => s + (Number(draft.component_weights?.[w.key]) || 0), 0)}
            </div>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mt-5">
          {WEIGHT_KEYS.map((w) => {
            const val = Number(draft.component_weights?.[w.key]) || 0;
            return (
              <div key={w.key} className="rounded-lg border border-[var(--ink-border)] p-4" style={{ backgroundColor: "var(--ink-1)" }}>
                <div className="flex items-center justify-between">
                  <div className="pr-3">
                    <Label className="text-white/80">{w.label}</Label>
                    <div className="text-xs text-white/50 mt-0.5">{w.note}</div>
                  </div>
                  <div className="font-display font-black text-2xl text-white font-mono-num min-w-[3ch] text-right" data-testid={`weight-value-${w.key}`}>
                    {val}
                  </div>
                </div>
                <Slider
                  className="mt-4"
                  min={0}
                  max={100}
                  step={5}
                  value={[val]}
                  onValueChange={(v) =>
                    setDraft({
                      ...draft,
                      component_weights: { ...(draft.component_weights || {}), [w.key]: Number(v?.[0] ?? 0) },
                    })
                  }
                  data-testid={`weight-${w.key}`}
                />
              </div>
            );
          })}
        </div>
      </section>

      <RecoveryVideoOverridesCard />
      <DigestScheduleCard />
      <NotificationPrefsCard />
      <PushSettingsCard />
      <StaffCard />

      <div className="sticky bottom-4 flex justify-end">
        <Button onClick={save} disabled={saving} size="lg" className="rounded-full px-8 text-white red-glow font-bold" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="save-settings-button">
          {saving ? "Saving…" : "Save settings"}
        </Button>
      </div>
    </div>
  );
}
